import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Check } from 'lucide-react';
import { collection, addDoc, deleteDoc, updateDoc, doc, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const TodoList = () => {
  const { currentUser } = useAuth();
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [loading, setLoading] = useState(true);

  // Real-time listener for tasks
  useEffect(() => {
    if (!currentUser) return;

    const tasksRef = collection(db, 'tasks');
    const q = query(tasksRef, where('userId', '==', currentUser.uid));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTodos(tasksData.sort((a, b) => a.createdAt - b.createdAt));
      setLoading(false);
    }, (error) => {
      console.error('Error fetching tasks:', error);
      setLoading(false); // Stop loading even on error
    });

    return () => unsubscribe();
  }, [currentUser]);

  const addTodo = async (e) => {
    e.preventDefault();
    if (!newTodo.trim()) return;

    try {
      const tasksRef = collection(db, 'tasks');
      await addDoc(tasksRef, {
        userId: currentUser.uid,
        text: newTodo.trim(),
        completed: false,
        createdAt: Date.now(),
      });
      setNewTodo('');
    } catch (error) {
      console.error('Error adding task:', error);
      alert('Failed to add task. Please try again.');
    }
  };

  const toggleTodo = async (todoId, currentStatus) => {
    try {
      const taskRef = doc(db, 'tasks', todoId);
      await updateDoc(taskRef, {
        completed: !currentStatus,
      });
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task. Please try again.');
    }
  };

  const deleteTodo = async (todoId) => {
    try {
      const taskRef = doc(db, 'tasks', todoId);
      await deleteDoc(taskRef);
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Todo Form */}
      <form onSubmit={addTodo} className="flex space-x-2">
        <input
          type="text"
          value={newTodo}
          onChange={(e) => setNewTodo(e.target.value)}
          placeholder="Add a new task..."
          className="input flex-1"
        />
        <button
          type="submit"
          className="btn-primary px-4"
        >
          <Plus className="h-5 w-5" />
        </button>
      </form>

      {/* Todo Items */}
      <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
        {todos.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-brand-brown/60">No tasks yet</p>
            <p className="text-sm text-brand-brown/40 mt-2">
              Add your first task to get organized!
            </p>
          </div>
        ) : (
          todos.map((todo) => (
            <div
              key={todo.id}
              className={`
                flex items-center space-x-3 p-3 rounded-xl border-2 transition-all duration-200
                ${todo.completed 
                  ? 'bg-pastel-green/20 border-pastel-green' 
                  : 'bg-white border-brand-brown/10 hover:border-pastel-blue'
                }
              `}
            >
              <button
                onClick={() => toggleTodo(todo.id, todo.completed)}
                className={`
                  flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200
                  ${todo.completed 
                    ? 'bg-pastel-green border-pastel-green' 
                    : 'border-brand-brown/30 hover:border-pastel-blue'
                  }
                `}
              >
                {todo.completed && <Check className="h-4 w-4 text-white" />}
              </button>

              <span
                className={`
                  flex-1 text-sm
                  ${todo.completed 
                    ? 'text-brand-brown/50 line-through' 
                    : 'text-brand-brown'
                  }
                `}
              >
                {todo.text}
              </span>

              <button
                onClick={() => deleteTodo(todo.id)}
                className="flex-shrink-0 p-2 rounded-lg hover:bg-red-100 text-red-500 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TodoList;
